import { useEffect, useState } from "react";
import { Close } from "../../Icons/Icons";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { useSelector } from "react-redux";
import { requestsData } from "../../reducer/dataSlice";
import { getCookie } from "../../reducer/utils";

const EditFutureOriginal = ({
  futureOriginal,
  showFilter,
  setShowFilter,
  notify,
  getFutureOriginal,
  isCreatingDuplicate,
}) => {
  const [isSeries, setIsSeries] = useState(false);
  const [changedFields, setChangedFields] = useState({});
  const [initialMappingCount, setInitialMappingCount] = useState(1);

  // Set up default form data structure with new fields
  const defaultFormData = {
    content_name: "",
    description: "",
    is_series: false,
    watch_url: null,
    upcoming_season_number: null,
    platform_season_number: null,
    episodes_count: null,
    provider: null,
    language_code: "",
    future_original_country_mapping: [
      {
        country_code: "",
        release_date_time: null,
        episodes_release_date_time: [],
      },
    ],
    source_details: [],
    imdb_id: "",
    tmdb_id: "",
    poster_path: "",
    alt_names: [],
    cast: [],
    directors: [],
    producers: [],
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const { loading } = useSelector(requestsData);

  useEffect(() => {
    if (futureOriginal) {
      // Deep clone to avoid mutating the prop
      const initialData = JSON.parse(JSON.stringify(futureOriginal));

      // Initialize the form data with the provided future original data
      // If it's a duplicate, clear the country code
      const initialMappings =
        initialData.future_original_country_mapping &&
        initialData.future_original_country_mapping.length > 0
          ? isCreatingDuplicate
            ? initialData.future_original_country_mapping?.map(
                (countryMapping) => {
                  return countryMapping?.release_dates?.length > 0
                    ? {
                        ...countryMapping,
                        country_code: "",
                        release_dates: countryMapping.release_dates.map(
                          (episodeData) => ({
                            ...episodeData,
                            is_verified: false,
                          }),
                        ),
                        notes: countryMapping.notes || "",
                      }
                    : {
                        ...countryMapping,
                        country_code: "",
                        release_dates: [],
                        movie_is_verified: false,
                        notes: countryMapping.notes || "",
                      };
                },
              )
            : initialData.future_original_country_mapping.map(
                (countryMapping) => ({
                  ...countryMapping,
                  notes: countryMapping.notes || "",
                }),
              )
          : [
              {
                country_code: "",
                release_date_time: null,
                release_dates: [],
                notes: "",
              },
            ];

      const dataToSet = {
        content_name: initialData.content_name || "",
        description: initialData.description || "",
        is_series: initialData.is_series || false,
        watch_url: initialData.watch_url || null,
        upcoming_season_number: initialData.upcoming_season_number || null,
        platform_season_number: initialData.platform_season_number || null,
        episodes_count: initialData.episodes_count || null,
        provider: initialData.provider || "",
        language_code: isCreatingDuplicate
          ? ""
          : initialData.language_code || "",
        future_original_country_mapping: initialMappings,
        source_details: Array.isArray(initialData.source_details)
          ? initialData.source_details
          : [],
        imdb_id: initialData.imdb_id || "",
        tmdb_id: initialData.tmdb_id || "",
        poster_path: initialData.poster_path || "",
        notes: initialData?.notes || "",
        // Add new fields here and handle potential non-array values
        alt_names: Array.isArray(initialData.alt_names)
          ? initialData.alt_names
          : [],
        cast: Array.isArray(initialData.cast) ? initialData.cast : [],
        directors: Array.isArray(initialData.directors)
          ? initialData.directors
          : [],
        producers: Array.isArray(initialData.producers)
          ? initialData.producers
          : [],
      };

      setFormData(dataToSet);
      setInitialMappingCount(initialMappings.length);
    } else {
      setFormData(defaultFormData);
      setInitialMappingCount(
        defaultFormData.future_original_country_mapping.length,
      );
    }
  }, [futureOriginal, showFilter]);

  const validateForm = () => {
    const errors = {};

    if (!formData.content_name.trim()) {
      errors.content_name = "Content Name is required";
    }
    if (!formData.description.trim()) {
      errors.description = "Description is required";
    }
    if (!formData.watch_url?.trim()) {
      errors.watch_url = "Watch URL is required";
    }
    if (!formData.provider.trim() || formData.provider === "Select") {
      errors.provider = "Provider is required";
    }
    if (formData.language_code.length === 0) {
      errors.language_code = "At least one language code is required";
    }

    // Validate each country mapping
    formData.future_original_country_mapping.forEach((mapping, index) => {
      if (!mapping.country_code) {
        errors[`country_code_${index}`] = "Country code is required";
      }

      if (formData.is_series) {
        if (!mapping.release_dates || mapping.release_dates.length === 0) {
          errors[`release_dates_${index}`] =
            "Episode release date is required for series";
        }
      } else {
        if (!mapping.release_date_time) {
          errors[`release_date_time_${index}`] =
            "Release date is required for non-series";
        }
      }
    });

    if (formData.is_series && !formData.upcoming_season_number) {
      errors.upcoming_season_number = "Season Number is required for series";
    }

    return errors;
  };

  const handleListInputChange = (e) => {
    const inputValue = e.target.value;

    // Split the input value by commas. This creates an array of strings.
    let value = inputValue.split(",");

    // The last item in the array might contain a trailing space.
    // We'll only trim the items if they are not the last one,
    // to allow the user to type a space after a comma.
    const lastIndex = value.length - 1;
    const processedValue = value.map((item, index) => {
      // Trim all items except the very last one.
      if (index !== lastIndex) {
        return item.trim();
      }
      // For the last item, just return it as is to preserve the space.
      return item;
    });

    // Now, filter out any empty strings that are not part of the user's active input.
    // An empty string at the end of the array (after the last comma) is valid input.
    const filteredValue = processedValue.filter((item, index) => {
      return item !== "" || index === lastIndex;
    });
    setFormData({
      ...formData,
      [e.target.name]: filteredValue,
    });
    setChangedFields({
      ...changedFields,
      [e.target.name]: true,
    });
  };

  // Function to update release dates for a specific country
  const updateReleaseDates = async (countryCode) => {
    const mapping = formData.future_original_country_mapping.find(
      (m) => m.country_code === countryCode,
    );
    if (!mapping) return;
    try {
      const payload = formData.is_series
        ? {
            episodes_release_dates: mapping?.release_dates.map(
              (episodeData) => {
                return episodeData?.is_manual_update
                  ? {
                      release_date_time: episodeData.release_date_time,
                      is_verified: episodeData.is_verified,
                      is_manual_update: episodeData.is_manual_update,
                    }
                  : {
                      release_date_time: episodeData.release_date_time,
                      is_verified: episodeData.is_verified,
                    };
              },
            ),
            delete_missing_episodes: true,
          }
        : {
            release_date: mapping.is_manual_update
              ? {
                  release_date_time: mapping.release_date_time,
                  is_verified: mapping.movie_is_verified,
                  is_manual_update: mapping.is_manual_update ?? false,
                }
              : {
                  release_date_time: mapping.release_date_time,
                  is_verified: mapping.movie_is_verified,
                },
            delete_missing_episodes: true,
          };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/internal/api/v4/vod/future-originals/${futureOriginal.id}?country_code=${countryCode}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          status: response.status,
          message: errorData.message || "Something went wrong",
        };
      }
    } catch (err) {
      console.error("Error:", err);
      throw err; // Re-throw to handle in the main submit function
    }
  };
  // Function to update general future original details
  const updateFutureOriginal = async () => {
    try {
      // Prepare payload without country mapping data
      const payload = {
        content_name: formData.content_name,
        description: formData.description,
        is_series: formData.is_series,
        watch_url: formData.watch_url,
        poster_path: formData.poster_path,
        upcoming_season_number: formData.upcoming_season_number,
        platform_season_number: formData.platform_season_number
          ? Number(formData.platform_season_number)
          : null,
        episodes_count: formData.episodes_count,
        provider: formData.provider,
        imdb_id: formData.imdb_id,
        tmdb_id: formData.tmdb_id,
        language_code: formData.language_code,
        source_details: formData.source_details,
        notes: formData?.notes,
        alt_names: formData.alt_names,
        cast: formData.cast,
        directors: formData.directors,
        producers: formData.producers,
      };

      if (isCreatingDuplicate) {
        // Add country mapping without release dates
        payload.future_original_country_mapping =
          formData?.future_original_country_mapping?.map((item) => {
            const { release_dates, ...restFields } = item;
            return restFields;
          });
      }
      const response = isCreatingDuplicate
        ? await fetch(
            `${process.env.REACT_APP_BASE_URL}/internal/api/v4/vod/future-originals`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${getCookie("token")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            },
          )
        : await fetch(
            `${process.env.REACT_APP_BASE_URL}/internal/api/v4/vod/future-originals/${futureOriginal.id}/update`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${getCookie("token")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            },
          );
      if (!response.ok) {
        const errorData = await response.json();
        throw {
          status: response.status,
          message: errorData?.message || "Something went wrong",
        };
      }
    } catch (err) {
      console.error("Error:", err);
      throw err; // Re-throw to handle in the main submit function
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const validationErrors = validateForm();
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        console.error("Validation errors:", validationErrors);
        return;
      }

      // First update the general information
      await updateFutureOriginal();

      // Then update release dates for each country
      if (!isCreatingDuplicate) {
        const promises = formData.future_original_country_mapping.map(
          (mapping) => updateReleaseDates(mapping.country_code),
        );
        await Promise.all(promises);
      }
      if (isCreatingDuplicate) {
        notify("Future Original created successfully!");
      } else {
        notify("Future Original updated successfully!");
      }
      setShowFilter(false);
      getFutureOriginal();
    } catch (err) {
      console.error("Error during submission:", err);
      notify(err.message || "Failed to submit Future Original");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setChangedFields({
      ...changedFields,
      [e.target.name]: true,
    });
  };

  const handleChangeCheckboxEdit = (e) => {
    const isSeriesValue = e.target.checked;

    // Update mapping fields based on is_series value
    const updatedMappings = formData.future_original_country_mapping.map(
      (mapping) => {
        if (isSeriesValue) {
          // If changing to series, initialize episodes_release_date_time if empty
          return {
            ...mapping,
            episodes_release_date_time:
              Array.isArray(mapping.episodes_release_date_time) &&
              mapping.episodes_release_date_time.length
                ? mapping.episodes_release_date_time
                : [mapping.release_date_time || null],
          };
        } else {
          // If changing to non-series, use first episode date as release_date_time if available
          return {
            ...mapping,
            release_date_time:
              mapping.release_date_time ||
              (Array.isArray(mapping.episodes_release_date_time) &&
              mapping.episodes_release_date_time.length
                ? mapping.episodes_release_date_time[0]
                : null),
          };
        }
      },
    );

    setFormData({
      ...formData,
      [e.target.name]: isSeriesValue,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      [e.target.name]: true,
      future_original_country_mapping: true,
    });
  };

  const handleCountryMappingChange = (index, field, value) => {
    const manualUpdatationFields = ["release_date_time", "movie_is_verified"];
    const updatedMappings = [...formData.future_original_country_mapping];
    updatedMappings[index] = manualUpdatationFields.includes(field)
      ? {
          ...updatedMappings[index],
          [field]: value,
          is_manual_update: true,
        }
      : {
          ...updatedMappings[index],
          [field]: value,
        };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      [field]: true,
    });
  };

  const removeCountryMapping = (index) => {
    if (formData.future_original_country_mapping.length <= 1) {
      notify("At least one country mapping is required");
      return;
    }

    const updatedMappings = [...formData.future_original_country_mapping];
    updatedMappings.splice(index, 1);

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
    });
  };

  const duplicateCountryMapping = (index) => {
    const mappingToDuplicate = formData.future_original_country_mapping[index];
    const duplicatedMapping = {
      ...mappingToDuplicate,
      country_code: "", // Clear the country code for the duplicate
    };

    const updatedMappings = [...formData.future_original_country_mapping];
    updatedMappings.splice(index + 1, 0, duplicatedMapping);

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
    });
  };

  const addEpisodeReleaseDate = (countryIndex) => {
    const updatedMappings = [...formData.future_original_country_mapping];
    const newReleaseDates = [
      ...updatedMappings[countryIndex].release_dates,
      { release_date_time: "", is_verified: false },
    ];
    const newEpisodesReleaseDateTime = [
      ...updatedMappings[countryIndex].episodes_release_date_time,
      "",
    ];

    updatedMappings[countryIndex] = {
      ...updatedMappings[countryIndex],
      release_dates: newReleaseDates,
      episodes_release_date_time: newEpisodesReleaseDateTime,
    };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      release_dates: true,
      episodes_release_date_time: true,
    });
  };

  const removeEpisodeReleaseDate = (countryIndex, episodeIndex) => {
    if (
      formData.future_original_country_mapping[countryIndex].release_dates
        .length <= 1
    ) {
      notify("At least one episode release date is required");
      return;
    }

    const updatedMappings = [...formData.future_original_country_mapping];
    const updatedEpisodeDates = [
      ...updatedMappings[countryIndex].release_dates,
    ];
    updatedEpisodeDates.splice(episodeIndex, 1);

    const updatedEpisodesReleaseDateTime = [
      ...updatedMappings[countryIndex].episodes_release_date_time,
    ];
    updatedEpisodesReleaseDateTime.splice(episodeIndex, 1);

    updatedMappings[countryIndex] = {
      ...updatedMappings[countryIndex],
      release_dates: updatedEpisodeDates,
      episodes_release_date_time: updatedEpisodesReleaseDateTime,
    };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      release_dates: true,
      episodes_release_date_time: true,
    });
  };

  const handleEpisodeDateChange = (countryIndex, episodeIndex, value) => {
    const updatedMappings = [...formData.future_original_country_mapping];
    const updatedEpisodeDates = [
      ...updatedMappings[countryIndex].release_dates,
    ];
    updatedEpisodeDates[episodeIndex]["release_date_time"] = value;
    updatedEpisodeDates[episodeIndex]["is_manual_update"] = true;

    const updatedEpisodesReleaseDateTime = [
      ...updatedMappings[countryIndex].episodes_release_date_time,
    ];
    updatedEpisodesReleaseDateTime[episodeIndex] = value;

    updatedMappings[countryIndex] = {
      ...updatedMappings[countryIndex],
      release_dates: updatedEpisodeDates,
      episodes_release_date_time: updatedEpisodesReleaseDateTime,
    };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      release_dates: true,
      episodes_release_date_time: true,
    });
  };

  const handleDragStart = (e, countryIndex, episodeIndex) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ countryIndex, episodeIndex }),
    );
    e.target.classList.add("opacity-50");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("border-primary1");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("border-primary1");
  };

  const handleDrop = (e, targetCountryIndex, targetEpisodeIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-primary1");

    const data = JSON.parse(e.dataTransfer.getData("text/plain"));
    const {
      countryIndex: sourceCountryIndex,
      episodeIndex: sourceEpisodeIndex,
    } = data;

    // Only allow reordering within the same country
    if (sourceCountryIndex !== targetCountryIndex) return;

    const updatedMappings = [...formData.future_original_country_mapping];
    const episodeDates = [...updatedMappings[sourceCountryIndex].release_dates];

    // Remove the episode from its original position
    const [movedEpisode] = episodeDates.splice(sourceEpisodeIndex, 1);
    // Insert it at the new position
    episodeDates.splice(targetEpisodeIndex, 0, movedEpisode);

    updatedMappings[sourceCountryIndex] = {
      ...updatedMappings[sourceCountryIndex],
      release_dates: episodeDates,
    };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      release_dates: true,
    });
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("opacity-50");
  };

  // Handler for episode verification checkbox changes
  const handleEpisodeVerificationChange = (
    countryIndex,
    episodeIndex,
    isChecked,
  ) => {
    const updatedMappings = [...formData.future_original_country_mapping];
    const updatedEpisodeReleaseDates = [
      ...updatedMappings[countryIndex].release_dates,
    ];

    updatedEpisodeReleaseDates[episodeIndex]["is_verified"] = isChecked;
    updatedEpisodeReleaseDates[episodeIndex]["is_manual_update"] = true;

    updatedMappings[countryIndex] = {
      ...updatedMappings[countryIndex],
      release_dates: updatedEpisodeReleaseDates,
    };

    setFormData({
      ...formData,
      future_original_country_mapping: updatedMappings,
    });

    setChangedFields({
      ...changedFields,
      future_original_country_mapping: true,
      release_dates: true,
    });
  };

  return (
    <div
      className={`fixed left-0 top-0 z-50 h-screen w-screen ${showFilter ? "visible" : "hidden"}`}
    >
      <div
        className="absolute h-full w-full bg-gray-800 bg-opacity-25 backdrop-blur"
        onClick={() => setShowFilter(false)}
      ></div>
      <div
        className={`absolute mx-auto h-screen w-full max-w-md rounded-lg bg-white shadow-lg ${showFilter ? "right-0" : "-right-full"} top-0`}
      >
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="text-lg font-semibold text-inherit">
            {isCreatingDuplicate ? "Create Request" : "Edit Future Original"}
          </h2>
          <Close
            className="cursor-pointer"
            onClick={() => setShowFilter(false)}
          />
        </div>
        <form
          className="flex flex-col justify-between p-5"
          style={{ height: "calc(100vh - 70px)" }}
          onSubmit={handleSubmit}
        >
          <div className="max-h-full overflow-auto">
            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Content Name
              </label>
              <input
                type="text"
                placeholder="Content Name"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.content_name}
                name="content_name"
                onChange={handleChange}
              />
              {errors.content_name && (
                <p className="text-sm text-red-600">{errors.content_name}</p>
              )}
            </div>
            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Description
              </label>
              <textarea
                placeholder="Description"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.description}
                name="description"
                onChange={handleChange}
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description}</p>
              )}
            </div>
            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Is Series
              </label>
              <ToggleSwitch
                name="is_series"
                value={formData.is_series}
                handleChange={handleChangeCheckboxEdit}
              />
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Watch URL
              </label>
              <input
                type="url"
                placeholder="https://example.com"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.watch_url || ""}
                name="watch_url"
                onChange={handleChange}
              />
              {errors.watch_url && (
                <p className="text-sm text-red-600">{errors.watch_url}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Poster Path
              </label>
              <input
                type="url"
                placeholder="https://example.com"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.poster_path || ""}
                name="poster_path"
                onChange={handleChange}
              />
              {errors.poster_path && (
                <p className="text-sm text-red-600">{errors.poster_path}</p>
              )}
            </div>

            {formData.is_series && (
              <>
                <div className="mb-2.5">
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    Upcoming Season Number
                  </label>
                  <input
                    type="number"
                    placeholder="1"
                    className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                    value={formData.upcoming_season_number}
                    onChange={handleChange}
                    name="upcoming_season_number"
                  />
                  {errors.upcoming_season_number && (
                    <p className="text-sm text-red-600">
                      {errors.upcoming_season_number}
                    </p>
                  )}
                </div>
                <div className="mb-2.5">
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    Platform Season Number
                  </label>
                  <input
                    type="number"
                    placeholder="1"
                    className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                    value={formData.platform_season_number ?? ""}
                    onChange={handleChange}
                    name="platform_season_number"
                  />
                </div>
                <div className="mb-2.5">
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    Episode Count
                  </label>
                  <input
                    type="number"
                    placeholder="10"
                    className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                    value={formData.episodes_count}
                    onChange={handleChange}
                    name="episodes_count"
                  />
                </div>
              </>
            )}
            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Provider
              </label>
              <select
                id="client-request-id"
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.provider}
                onChange={handleChange}
                name="provider"
              >
                <option value="">Select</option>
                <option value="netflix">netflix</option>
                <option value="youtube_tv">youtube_tv</option>
                <option value="max">max</option>
                <option value="paramount_plus">paramount_plus</option>
                <option value="starz">starz</option>
                <option value="cinemax">cinemax</option>
                <option value="mgm_plus">mgm_plus</option>
                <option value="hallmark">hallmark</option>
                <option value="hbo">hbo</option>
                <option value="amc_plus">amc_plus</option>
                <option value="hulu">hulu</option>
                <option value="disney">disney</option>
                <option value="prime">prime</option>
                <option value="apple_tv">apple_tv</option>
                <option value="peacocktv">peacocktv</option>
                <option value="discovery_plus">discovery_plus</option>
                <option value="britbox">britbox</option>
                <option value="fx">fx</option>
                <option value="showtime">showtime</option>
              </select>
              {errors.provider && (
                <p className="text-sm text-red-600">{errors.provider}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                IMDB ID
              </label>
              <input
                type="text"
                placeholder="XXX"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.imdb_id || ""}
                name="imdb_id"
                onChange={handleChange}
              />
              {errors.imdb_id && (
                <p className="text-sm text-red-600">{errors.imdb_id}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                TMDB ID
              </label>
              <input
                type="text"
                placeholder="XXX"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.tmdb_id || ""}
                name="tmdb_id"
                onChange={handleChange}
              />
              {errors.tmdb_id && (
                <p className="text-sm text-red-600">{errors.tmdb_id}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Language Code
              </label>
              <input
                type="text"
                placeholder="en"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.language_code}
                name="language_code"
                onChange={handleChange}
              />
              {errors.language_code && (
                <p className="text-sm text-red-600">{errors.language_code}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Source Details (comma separated)
              </label>
              <input
                type="text"
                placeholder="Source Details (comma separated)"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.source_details}
                name="source_details"
                onChange={handleListInputChange}
              />
              {errors.source_details && (
                <p className="text-sm text-red-600">{errors.source_details}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Alternative Names (comma separated)
              </label>
              <input
                type="text"
                placeholder="Alternative Names"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.alt_names}
                name="alt_names"
                onChange={handleListInputChange}
              />
              {errors.alt_names && (
                <p className="text-sm text-red-600">{errors.alt_names}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Cast (comma separated)
              </label>
              <input
                type="text"
                placeholder="Cast"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.cast}
                name="cast"
                onChange={handleListInputChange}
              />
              {errors.cast && (
                <p className="text-sm text-red-600">{errors.cast}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Directors (comma separated)
              </label>
              <input
                type="text"
                placeholder="Directors"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.directors}
                name="directors"
                onChange={handleListInputChange}
              />
              {errors.directors && (
                <p className="text-sm text-red-600">{errors.directors}</p>
              )}
            </div>

            <div className="mb-2.5">
              <label className="mb-2 block text-sm font-medium text-gray-900">
                Producers (comma separated)
              </label>
              <input
                type="text"
                placeholder="Producers"
                className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                value={formData.producers}
                name="producers"
                onChange={handleListInputChange}
              />
              {errors.producers && (
                <p className="text-sm text-red-600">{errors.producers}</p>
              )}
            </div>

            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-900">
                  Country Mappings
                </label>
              </div>

              {formData.future_original_country_mapping.map(
                (mapping, index) => {
                  const isMovieAlreadyVerified =
                    futureOriginal?.future_original_country_mapping?.find(
                      (countryData) =>
                        countryData?.country_code === mapping.country_code,
                    )?.movie_is_verified;
                  return (
                    <div
                      key={index}
                      className="mb-3 rounded-lg border bg-gray-50 p-3"
                    >
                      <div className="mb-2">
                        <div className="flex items-center justify-between">
                          <label className="mb-1 block text-sm font-medium text-gray-900">
                            Country Code
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => duplicateCountryMapping(index)}
                              className="text-sm text-primary1"
                            >
                              Duplicate
                            </button>
                            {formData.future_original_country_mapping.length >
                              1 && (
                              <button
                                type="button"
                                onClick={() => removeCountryMapping(index)}
                                className="text-sm text-red-600"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        <input
                          type="text"
                          placeholder="Country Code"
                          className="block w-full rounded-md border-gray-300 bg-white px-4 py-2 shadow-sm"
                          value={mapping.country_code}
                          onChange={(e) =>
                            handleCountryMappingChange(
                              index,
                              "country_code",
                              e.target.value,
                            )
                          }
                        />
                        {errors[`country_code_${index}`] && (
                          <p className="text-sm text-red-600">
                            {errors[`country_code_${index}`]}
                          </p>
                        )}
                      </div>

                      {formData.is_series ? (
                        <div className="mb-1">
                          <div className="mb-2 flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">
                              Episode Release Dates
                            </label>
                            <button
                              type="button"
                              onClick={() => addEpisodeReleaseDate(index)}
                              className="rounded-lg bg-primary1 px-2 py-1 text-xs text-white"
                            >
                              Add Date
                            </button>
                          </div>

                          {mapping.episodes_release_date_time.length === 0 && (
                            <div className="mb-2">
                              <p className="text-sm text-gray-600">
                                No episode dates added yet
                              </p>
                              <button
                                type="button"
                                onClick={() => addEpisodeReleaseDate(index)}
                                className="mt-1 text-sm text-primary1 underline"
                              >
                                Add your first episode date
                              </button>
                            </div>
                          )}

                          {mapping.release_dates.map(
                            (formEpisodeData, episodeIndex) => {
                              const isEpisodeAlreadyVerified =
                                futureOriginal?.future_original_country_mapping
                                  ?.find(
                                    (countryData) =>
                                      countryData?.country_code ===
                                      mapping.country_code,
                                  )
                                  ?.release_dates?.find(
                                    (episodeData) =>
                                      episodeData?.id === formEpisodeData.id,
                                  )?.is_verified;

                              return (
                                <div
                                  key={episodeIndex}
                                  className="mb-2 flex items-center gap-2"
                                  draggable={!isEpisodeAlreadyVerified}
                                  onDragStart={
                                    !isEpisodeAlreadyVerified
                                      ? (e) =>
                                          handleDragStart(
                                            e,
                                            index,
                                            episodeIndex,
                                          )
                                      : undefined
                                  }
                                  onDragOver={
                                    !isEpisodeAlreadyVerified
                                      ? handleDragOver
                                      : undefined
                                  }
                                  onDragLeave={
                                    !isEpisodeAlreadyVerified
                                      ? handleDragLeave
                                      : undefined
                                  }
                                  onDrop={
                                    !isEpisodeAlreadyVerified
                                      ? (e) =>
                                          handleDrop(e, index, episodeIndex)
                                      : undefined
                                  }
                                  onDragEnd={
                                    !isEpisodeAlreadyVerified
                                      ? handleDragEnd
                                      : undefined
                                  }
                                >
                                  <div className="flex-grow">
                                    <div className="mb-1 flex items-center">
                                      <div
                                        className={`flex items-center ${isEpisodeAlreadyVerified ? "cursor-default" : "cursor-move"}`}
                                      >
                                        <svg
                                          className="mr-2 h-4 w-4 text-gray-400"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 8h16M4 16h16"
                                          />
                                        </svg>
                                        <span className="text-xs text-gray-600">
                                          Episode #{episodeIndex + 1}
                                        </span>
                                      </div>
                                      {mapping.release_dates.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeEpisodeReleaseDate(
                                              index,
                                              episodeIndex,
                                            )
                                          }
                                          className="ml-2 cursor-pointer text-xs text-red-500"
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="datetime-local"
                                        className="block flex-grow rounded-md border-gray-300 bg-white px-4 py-2 shadow-sm"
                                        value={
                                          formEpisodeData?.release_date_time ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleEpisodeDateChange(
                                            index,
                                            episodeIndex,
                                            e.target.value,
                                          )
                                        }
                                      />
                                      {/* Don't show verification checkbox if creating a duplicate */}
                                      {!isCreatingDuplicate && (
                                        <div className={`flex items-center `}>
                                          <input
                                            id={`verify-${formEpisodeData?.id ?? episodeIndex}`}
                                            type="checkbox"
                                            className={`h-4 w-4 rounded border-gray-300 bg-gray-100 ${isEpisodeAlreadyVerified ? "text-gray-400" : "text-primary1"} focus:ring-2 focus:ring-primary1 ${isEpisodeAlreadyVerified ? "cursor-not-allowed" : "cursor-pointer"}`}
                                            checked={
                                              formEpisodeData?.is_verified ??
                                              false
                                            }
                                            onChange={(e) =>
                                              handleEpisodeVerificationChange(
                                                index,
                                                episodeIndex,
                                                e.target.checked,
                                              )
                                            }
                                            disabled={isEpisodeAlreadyVerified}
                                          />
                                          <label
                                            htmlFor={`verify-${formEpisodeData?.id ?? episodeIndex}`}
                                            className={`ml-1 text-xs text-gray-600 ${isEpisodeAlreadyVerified ? "cursor-not-allowed" : "cursor-pointer"}`}
                                          >
                                            Verified
                                          </label>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}

                          {errors[`episodes_release_date_time_${index}`] && (
                            <p className="text-sm text-red-600">
                              {errors[`episodes_release_date_time_${index}`]}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mb-1">
                          <label className="mb-1 block text-sm font-medium text-gray-900">
                            Release Date
                          </label>
                          <div className={`flex items-center gap-2`}>
                            <input
                              type="datetime-local"
                              className="block w-full rounded-md border-gray-300 bg-white px-4 py-2 shadow-sm"
                              value={mapping.release_date_time || ""}
                              onChange={(e) =>
                                handleCountryMappingChange(
                                  index,
                                  "release_date_time",
                                  e.target.value,
                                )
                              }
                              disabled={isMovieAlreadyVerified}
                            />
                            <div className="flex items-center">
                              {" "}
                              <input
                                type="checkbox"
                                id={`verify-${futureOriginal.id}`}
                                className={`h-4 w-4 rounded border-gray-300 bg-gray-100 ${isMovieAlreadyVerified ? "text-gray-400" : "text-primary1"} focus:ring-2 focus:ring-primary1 ${isMovieAlreadyVerified ? "cursor-not-allowed" : "cursor-pointer"}`}
                                checked={mapping.movie_is_verified}
                                onChange={(e) =>
                                  handleCountryMappingChange(
                                    index,
                                    "movie_is_verified",
                                    e.target.checked,
                                  )
                                }
                                disabled={isMovieAlreadyVerified}
                              />
                              <label
                                htmlFor={`verify-${futureOriginal.id}`}
                                className={`ml-1 text-xs text-gray-600 ${isMovieAlreadyVerified ? "cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                Verified
                              </label>
                            </div>
                          </div>

                          {errors[`release_date_time_${index}`] && (
                            <p className="text-sm text-red-600">
                              {errors[`release_date_time_${index}`]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>
            {/* Notes field for each country mapping */}
            <div className="mb-2">
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Notes
              </label>
              <textarea
                name="notes"
                className="block w-full rounded-md border-gray-300 bg-white px-3 py-2 text-xs shadow-sm"
                placeholder="Add notes for this country..."
                value={formData?.notes || ""}
                onChange={handleChange}
                rows={2}
              />
            </div>
          </div>
          <div className="flex items-center justify-end border-t pt-4">
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-primary1 px-3 py-2 text-white"
            >
              {isCreatingDuplicate ? "Add" : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFutureOriginal;
